// QR Kiosk v2 — KV2-7 task LIST (/public-v2/machines/:id/tasks). Two-surface page
// (machine = outer context, tasks = body), structured EXACTLY like ReportsListV2:
// route-scope guard → viewer gate → §5 4-branch tree → overdue-pinned two-group body.
// Server order (dueDate asc, nulls last) preserved inside each group; client never
// filters rows away (done rows render struck-through for all roles). Tokens only.
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import { Roles } from "../../../account/models/AccountModels";
import { getMachine } from "../../publicSlice";
import { KioskTask } from "./kioskTasksModels";
import { getKioskTasks } from "./kioskTasksSlice";
import {
  isManagerOrAbove,
  openTaskCount,
  secondGroupEyebrowKey,
  splitTaskGroups,
  TASK_DUE_FORMAT,
  taskScopeKey,
} from "./taskUtils";
import { KPage } from "../shared/KPage";
import { Glyph, GD } from "../shared/glyphs";
import { EmptyCard } from "../shared/EmptyCard";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { useKioskRouteMachine } from "../shared/useKioskRouteMachine";
import { formatClockTime } from "../shared/clock";
import { usePressed } from "../shared/usePressed";
import { KDate } from "../reports/ReportBits";
import { REPORT_DATE_FORMAT } from "../reports/reportUtils";
import { TaskStatusChip } from "./TaskBits";
import { TasksListSkeleton } from "./TaskSkeletons";

interface TaskRowProps {
  task: KioskTask;
  index: number;
  manager: boolean;
  onOpen: () => void;
}

// Local component (per-row usePressed, KV2-2-j rule). Clone of the committed ReportRow.
function TaskRow({ task, index, manager, onOpen }: TaskRowProps) {
  const { pressHandlers, pressStyle } = usePressed();
  const { translate } = useTranslation("public");
  const done = task.kioskStatus === "done";
  const accent = task.isOverdue
    ? "--red"
    : done
    ? "--border"
    : task.kioskStatus === "todo"
    ? "--tier-accent"
    : "--amber";
  const metaColor = task.isOverdue ? "color-mix(in oklab, var(--red) 78%, var(--ink))" : "var(--faint)";
  return (
    <button
      type="button"
      {...pressHandlers}
      onClick={onOpen}
      className="kv2-enter"
      style={{
        width: "100%", border: 0, textAlign: "left", fontFamily: "inherit", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        background: "var(--card)", position: "relative",
        borderTop: index === 0 ? "none" : "1px solid var(--border-soft)",
        opacity: done ? 0.62 : 1, animationDelay: `${Math.min(index * 0.05, 0.4)}s`, ...pressStyle,
      }}
    >
      <span
        style={{
          position: "absolute", left: 0, top: 8, bottom: 8, width: 3,
          borderRadius: "0 3px 3px 0", background: `var(${accent})`,
        }}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 600, color: "var(--tier-accent)" }}>
            #{task.number}
          </span>
          <TaskStatusChip kioskStatus={task.kioskStatus} isOverdue={task.isOverdue} />
        </span>
        <span
          style={{
            display: "block", fontSize: 14.5, fontWeight: 600, color: "var(--ink)", marginTop: 3,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            textDecoration: done ? "line-through" : "none",
          }}
        >
          {task.title}
        </span>
        <span
          style={{
            display: "block", fontFamily: "var(--font-mono)", fontSize: 10,
            color: metaColor, marginTop: 3, letterSpacing: "0.03em",
          }}
        >
          {translate("kioskV2TaskDue").toLowerCase()} <KDate date={task.dueDate} format={TASK_DUE_FORMAT} />
          {task.actualEndDate && (
            <>
              {" · "}
              {translate("kioskV2TaskClosed").toLowerCase()} <KDate date={task.actualEndDate} format={REPORT_DATE_FORMAT} />
            </>
          )}
          {manager && task.assigneeName && <>{" · "}{task.assigneeName}</>}
        </span>
      </span>
      <span style={{ color: "var(--faint)", display: "inline-flex" }}>
        <Glyph d={GD.chev} size={16} />
      </span>
    </button>
  );
}

const groupCardStyle = (overdue: boolean): React.CSSProperties => ({
  margin: "0 14px",
  border: `1px solid ${overdue ? "color-mix(in oklab, var(--red) 32%, var(--border))" : "var(--border)"}`,
  borderRadius: 12,
  boxShadow: "var(--shadow)",
  overflow: "hidden",
  flexShrink: 0,
});

export function TasksListV2() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const user = useAppSelector((s) => s.account.user);
  const role = user?.role;
  const isViewer = role === Roles.VIEWER;
  const manager = isManagerOrAbove(role);
  const { tasks, tasksMachineId } = useAppSelector((s) => s.kioskTasks);

  // Route-scoped machine (KV2-1-e) via the shared resolver — resolves + backfills the
  // guest's curated payload so tasks fetch (by GUID) and nav (by internalNumber) both work.
  const m = useKioskRouteMachine(id);

  useEffect(() => {
    if (isViewer) return; // client viewer gate: never fire the doomed request (§1.2)
    if (id && !m) dispatch(getMachine(id));
  }, [id, m, isViewer, dispatch]);

  const fetchTasks = () => {
    if (m?.id) dispatch(getKioskTasks({ machineId: m.id }));
  };
  useEffect(() => {
    if (isViewer) return;
    if (m?.id) dispatch(getKioskTasks({ machineId: m.id }));
  }, [m?.id, isViewer, dispatch]);

  const stMachine = useSurfaceState("machine", id);
  const stTasks = useSurfaceState("tasks", m?.id);

  // BUG-3: scope by the tasks surface's fulfilled key (the machineId the cache was loaded
  // for) AND the slice's machineId guard — an empty result cached for machine B never
  // reads as "machine A has no tasks".
  const scopedTasks =
    tasks != null && stTasks.loadedForKey && tasksMachineId === m?.id.toLowerCase() ? tasks : undefined;

  // Viewer client gate — no fetch, no task network calls; back bar stays reachable (§8.2).
  if (isViewer) {
    return (
      <KPage titleKey="kioskV2TasksTitle" glyph={GD.task} machineNumber={id}>
        <EmptyCard
          glyph={GD.lock}
          title={translate("kioskV2TaskNoAccessTitle")}
          hint={translate("kioskV2TaskNoAccessHint")}
        />
      </KPage>
    );
  }

  const loadErr = (onRetry: () => void) => (
    <ErrorCallout
      title={translate("kioskV2TasksLoadFailedTitle")}
      hint={translate("kioskV2DashboardLoadFailedHint")}
      onRetry={onRetry}
      retryLabel={translate("kioskV2Retry")}
    />
  );

  const workerName = user ? `${user.firstName} ${user.lastName}`.trim() : "";

  const renderNormalBody = (): React.ReactNode => {
    const list = scopedTasks ?? [];
    if (list.length === 0) {
      return manager ? (
        <EmptyCard glyph={GD.task} title={translate("kioskV2TasksEmptyManagerTitle")} hint={translate("kioskV2TasksEmptyManagerHint")} />
      ) : (
        <EmptyCard glyph={GD.task} title={translate("kioskV2TasksEmptyWorkerTitle")} hint={translate("kioskV2TasksEmptyWorkerHint")} />
      );
    }
    const { overdue, rest } = splitTaskGroups(list);
    return (
      <>
        <div
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--muted)",
            padding: "2px 16px 0", letterSpacing: "0.04em",
          }}
        >
          {m ? getMachineDisplayName(m) : ""} · {translate(taskScopeKey(role), { name: workerName })}
        </div>

        {overdue.length > 0 && (
          <>
            <div className="eyebrow" style={{ color: "color-mix(in oklab, var(--red) 78%, var(--ink))", padding: "14px 18px 6px" }}>
              {translate("kioskV2TasksOverdue")} · {overdue.length}
            </div>
            <div style={groupCardStyle(true)}>
              {overdue.map((task, i) => (
                <TaskRow key={task.number} task={task} index={i} manager={manager} onOpen={() => navigate(`/public-v2/tasks/${task.number}`)} />
              ))}
            </div>
          </>
        )}

        {rest.length > 0 && (
          <>
            <div className="eyebrow" style={{ color: "var(--muted)", padding: "14px 18px 6px" }}>
              {translate(secondGroupEyebrowKey(role))}
            </div>
            <div style={groupCardStyle(false)}>
              {rest.map((task, i) => (
                <TaskRow key={task.number} task={task} index={i} manager={manager} onOpen={() => navigate(`/public-v2/tasks/${task.number}`)} />
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  // §5 tree over two surfaces (machine outer, tasks body). BUG-1: skeleton covers idle AND pending.
  let body: React.ReactNode;
  if (!m) {
    body = stMachine.failed ? loadErr(() => { if (id) dispatch(getMachine(id)); }) : <TasksListSkeleton />;
  } else if (!scopedTasks) {
    body = stTasks.failed ? loadErr(fetchTasks) : <TasksListSkeleton />;
  } else {
    const machineFailed = stMachine.failed; // outer surface wins the callout when both failed
    if (machineFailed || stTasks.failed) {
      body = (
        <CachedErrorState
          title={translate("kioskV2TasksLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={machineFailed ? () => { if (id) dispatch(getMachine(id)); } : fetchTasks}
          retryLabel={translate("kioskV2Retry")}
          cachedAt={stTasks.loadedAt}
          cachedLabel={stTasks.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(stTasks.loadedAt) }) : undefined}
        >
          {renderNormalBody()}
        </CachedErrorState>
      );
    } else {
      body = renderNormalBody();
    }
  }

  const openCount = openTaskCount(scopedTasks ?? []);

  return (
    <KPage titleKey="kioskV2TasksTitle" glyph={GD.task} count={openCount > 0 ? openCount : undefined} machineNumber={id}>
      {body}
    </KPage>
  );
}

export default TasksListV2;
